import {
  MAX_TENANT_SLUG_LENGTH,
  normaliseTenantSlug,
  RESERVED_TENANT_SLUGS,
  type TenantSlugProblem,
  tenantSlugProblem,
} from '@t4s/domain'
import type { Deployment } from '../shell/environment.ts'

/**
 * The slug field's behaviour: the rule, its copy, and the hostname it produces.
 *
 * **The rule itself is `packages/domain`'s**, not a second copy of it. §9.2 asks for the
 * rule to be enforced in the field it is typed in *and* on the server, and the only way
 * those two cannot drift is for both to call the same function — which is why
 * `tenantSlugProblem` lives in the package that imports nothing and is reachable from
 * both. What is here is the half that belongs to a screen: the sentence each problem
 * shows, and the hostname preview that makes the rule obvious before anybody reads it.
 *
 * The API's own `message` is never rendered (§20.1), which applies to a validation
 * failure's `details[]` as much as to a notice — so the sentences below are this
 * application's, keyed by the same problem the domain reports.
 */

/**
 * The zone a tenant's app is published on, per estate. `docs/ENVIRONMENTS.md` §8.
 *
 * Production is the **bare** form, which is the point of the scheme — a tenant's own
 * customers never read an environment name — and pre-prod is one label deeper, which is
 * why `preprod` is one of the labels a slug may not be. `local` is absent on purpose: a
 * local API publishes no tenant anywhere.
 *
 * **One estate's console names one of these, never both.** Pre-prod and prod are disjoint
 * estates with their own databases: a tenant created on prod exists only on prod, and
 * `<slug>.preprod.tasks4staff.com` does not serve it — it is either nothing at all, or a
 * *different company* holding the same slug in the other estate. Printing both on a prod
 * console is therefore not noise but a false statement, and it blurs exactly the thing
 * §5.1's environment label exists to keep sharp on a screen whose actions cannot be undone.
 *
 * This is the only place these strings are written. Nothing in JSX spells a hostname.
 *
 * The `satisfies` is the guarantee, not a formality: the keys are checked against the deployment
 * names the masthead's label is read from, so a zone can never be filed under an estate that does
 * not exist, and renaming a deployment fails to compile here rather than silently addressing
 * nobody. (`Partial`, because `local` publishes no tenant and must not be forced to name a zone.)
 */
export const TENANT_APP_ZONES = {
  prod: 'tasks4staff.com',
  preprod: 'preprod.tasks4staff.com',
} as const satisfies Partial<Record<Deployment, string>>

/** The estates a tenant app is actually published in — `local` is not one. */
export type PublishedDeployment = keyof typeof TENANT_APP_ZONES & Deployment

/** In the order a candidate list reads them: the bare address first. */
export const PUBLISHED_DEPLOYMENTS: readonly PublishedDeployment[] = ['prod', 'preprod']

/** One address, and the estate it would be an address in. */
export type TenantAddress = {
  readonly deployment: PublishedDeployment
  readonly hostname: string
}

/**
 * `a, b and c` — a list a person reads, from a list a validator holds.
 *
 * §9.1 asks for the reserved labels **in full** rather than hinted at, and the list grew
 * from two to four with the rename to Tasks4Staff. `join(' and ')` was fine for two and is
 * not a sentence for four.
 */
function listOf(items: readonly string[]): string {
  return items.length < 2
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/** Where the field starts showing a character counter (§9.1). */
export const SLUG_COUNTER_THRESHOLD = 50

/**
 * One sentence per way a slug can be wrong.
 *
 * The dot gets its own reason, because it is the rule an owner is likeliest to try and
 * the only one whose *why* is not guessable from the rule (§9.1).
 */
export const SLUG_PROBLEM_MESSAGES: Readonly<Record<TenantSlugProblem, string>> = {
  blank: 'Required.',
  dotted: 'A dot would make this two labels, and the certificate only covers one.',
  charset: 'Use lowercase letters, digits and hyphens.',
  hyphen_edge: 'A slug cannot start or end with a hyphen.',
  too_long: `${MAX_TENANT_SLUG_LENGTH} characters at most.`,
  reserved: `${listOf(RESERVED_TENANT_SLUGS.map((slug) => `“${slug}”`))} are the platform’s own addresses.`,
}

/**
 * What the field should say about this value, or `undefined` when it is usable.
 *
 * It does **not** validate a value the owner has not finished typing: an empty field
 * before the first keystroke is not an error, and marking it as one on load is how a form
 * greets somebody with a red message about something they have not done. `blank` is
 * reported only when the field has been touched.
 */
export function slugFieldError(value: string, touched: boolean): string | undefined {
  const problem = tenantSlugProblem(normaliseTenantSlug(value))
  if (problem === undefined) {
    return undefined
  }
  if (problem === 'blank' && !touched) {
    return undefined
  }
  return SLUG_PROBLEM_MESSAGES[problem]
}

/**
 * The value as the field should hold it after a keystroke.
 *
 * An uppercase letter is **corrected in place** rather than refused (§9.1): a hostname is
 * case-insensitive, and refusing a capital would be pedantry. Nothing else is corrected —
 * a dot stays a dot, and the message beside the field explains it, because a slug the
 * owner did not type is a hostname they will not recognise.
 */
export function correctSlugInput(value: string): string {
  return value.toLowerCase()
}

/**
 * This tenant's address in the estate this console is pointed at (§9.2's live preview).
 *
 * **One entry when the estate is known**, which is the whole of the fix: a prod console
 * names the prod hostname and nothing else, a pre-prod console the pre-prod one.
 *
 * `local` and an unknown estate return **both**, as candidates rather than as addresses —
 * neither is knowable to be this tenant's, so the caller's copy states them
 * conditionally ("… on prod, or … on pre-prod") and never as a fact. Guessing one would be
 * the same error in a quieter voice: on an unlabelled prod tab it would name pre-prod, the
 * reading the masthead's "environment unknown" exists to refuse.
 */
/*
 * `deployment` is a plain `string | undefined` rather than `Deployment | undefined` because the
 * value ultimately comes off the wire: `/health` can report an estate name this build has never
 * heard of, and the narrow type would only push the moment of judgement to whoever converts it.
 *
 * What makes that safe is **not** the type. It is the branch structure below: every value this
 * function does not recognise — a future estate name, a typo, `undefined` — falls through the
 * filter to the both-candidates list. There is no path on which an unrecognised estate produces
 * one hostname, so the failure mode is "says it cannot tell", never "names the wrong estate".
 * That property is what the tests pin, and it is what any change here must preserve.
 */
export function tenantAddresses(
  slug: string,
  deployment: string | undefined,
): readonly TenantAddress[] {
  const normalised = normaliseTenantSlug(slug)
  const label = normalised === '' ? '<slug>' : normalised
  const shown = PUBLISHED_DEPLOYMENTS.filter(
    (candidate) => deployment === undefined || deployment === candidate,
  )
  return (shown.length === 0 ? PUBLISHED_DEPLOYMENTS : shown).map((candidate) => ({
    deployment: candidate,
    hostname: `${label}.${TENANT_APP_ZONES[candidate]}`,
  }))
}

/**
 * A name as a suggested slug.
 *
 * The suggestion is a convenience and never a correction: it stops the moment the owner
 * types in the slug field (§8.2), and it can perfectly well produce something the rule
 * refuses — a company called "Reinigung 2000 & Söhne" has no obvious label — in which
 * case the field says so and the owner types one.
 */
export function suggestSlug(name: string): string {
  return normaliseTenantSlug(name)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_TENANT_SLUG_LENGTH)
    .replace(/-+$/, '')
}
